/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

// System defined variables
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

// Custom defined variables
import Grid from '@components/base/grid/Grid'
import CheckboxControl from '@components/base/checkboxControl/CheckboxControl'
import ButtonComponent from '@components/base/button/Button'
import { searchBarColumnPreferences } from '@recoil/atoms/atomsWithLocalStorage'
import { fetchDataSelector } from '@recoil/selectors/selectors'
import {
  selectedContractDetails,
  toastMessage,
  selectedCaseDetails,
  madeChanges,
  additionalOptionsDisabled,
  lastPathName,
  caseOverviewTabSelectedIndexAtom,
} from '@recoil/atoms/atoms'
import APIEndPoints from '@models/api/apiEndpoints'
import ColumnFilterIcon from '@assets/images/columnFilterIcon.svg'
import utils from '@utils/utils'

import './SearchBarResultGrid.css'

const pickerFields = [
  {
    field: 'caseNumber',
    headerName: 'Case Number',
    type: 'text',
    alwaysVisible: true,
  },
  {
    field: 'organisation',
    headerName: 'Organization Name',
    type: 'text',
    alwaysVisible: false,
  },
  {
    field: 'contract',
    headerName: 'Contract Name',
    type: 'text',
    alwaysVisible: false,
  },

  { field: 'vrm', headerName: 'VRM', type: 'text', alwaysVisible: false },
  {
    field: 'contraventionCode',
    headerName: 'Contravention Code',
    type: 'text',
    alwaysVisible: false,
  },
  {
    field: 'location',
    headerName: 'Location',
    type: 'text',
    alwaysVisible: false,
  },
  { field: 'stage', headerName: 'Stage', type: 'text', alwaysVisible: false },
  { field: 'status', headerName: 'Status', type: 'text', alwaysVisible: false },
  {
    field: 'serviceMethod',
    headerName: 'Service Method',
    type: 'text',
    alwaysVisible: false,
  },
  { field: 'source', headerName: 'Source', type: 'text', alwaysVisible: false },
  {
    field: 'vehicleColor',
    headerName: 'Vehicle Colour',
    type: 'text',
    alwaysVisible: false,
  },
  {
    field: 'vehicleMake',
    headerName: 'Vehicle Make',
    type: 'text',
    alwaysVisible: false,
  },
  {
    field: 'warningFlag',
    headerName: 'Warning Flag',
    type: 'text',
    alwaysVisible: false,
  },
  {
    field: 'createdOn',
    headerName: 'PCN Created Date',
    type: 'date',
    alwaysVisible: false,
  },
  {
    field: 'contraventionDate',
    headerName: 'Contravention Date',
    type: 'date',
    alwaysVisible: false,
  },
]

// Maps internal field names to API field names for columnOption serialization
const fieldNameToApi = { vrm: 'vrmNo' }
const fieldNameFromApi = { vrmNo: 'vrm' }

/* istanbul ignore next */
const customTextRenderer = (params) => {
  return params?.value || ''
}

/* istanbul ignore next */
const customDateRenderer = (params) => {
  return params?.value || ''
}

function SearchBarResultGrid({ searchData, contractType, onModalClose }) {
  const [columnPreferences, setColumnPreferences] = useRecoilState(
    searchBarColumnPreferences
  )
  const getCoreData = useRecoilValue(fetchDataSelector)
  const selectedContract = useRecoilValue(selectedContractDetails)
  const showToastMessage = useRecoilValue(toastMessage)
  const setSelectedcaseDetails = useSetRecoilState(selectedCaseDetails)
  const setChangesMade = useSetRecoilState(madeChanges)
  const setAdditionalOptionsDisabled = useSetRecoilState(
    additionalOptionsDisabled
  )
  const setLastPathName = useSetRecoilState(lastPathName)
  const resetCaseOverviewTabSelectedIndex = useSetRecoilState(
    caseOverviewTabSelectedIndexAtom
  )
  const navigate = useNavigate()
  const [showColumnFilter, setShowColumnFilter] = useState(false)
  const [tempColumnState, setTempColumnState] = useState({})
  const [pageSize, setPageSize] = useState(10)
  const gridApiRef = useRef(null)
  const containerRef = useRef(null)
  const [popupParentEl, setPopupParentEl] = useState(null)

  /* istanbul ignore next */
  useEffect(() => {
    if (containerRef?.current) {
      const modalParent = containerRef?.current?.closest('.modalmainpage')
      if (modalParent) {
        setPopupParentEl(modalParent)
      }
    }
  }, [])

  /* istanbul ignore next */
  const onCaseNumberClick = (caseData) => {
    const isLockedCase =
      caseData?.isCaseLocked === true ||
      caseData?.isCaseLocked?.toString()?.toLowerCase()?.trim() === 'true'

    if (isLockedCase) {
      onModalClose?.()
      navigate('/lockedcase', {
        state: {
          searchedTerm: caseData?.reference || caseData?.caseNumber,
          lockMessage: 'This case is locked and under  review.',
        },
      })
      return
    }

    setChangesMade(false)
    setAdditionalOptionsDisabled(true)
    setSelectedcaseDetails(caseData)
    resetCaseOverviewTabSelectedIndex(0)
    onModalClose?.()
    if (caseData?.isPreCase) {
      navigate(`/precasedetail?preCaseId=${utils?.encryptData(caseData?.id)}`)
    } else {
      navigate(`/pcnpage?caseId=${utils?.encryptData(caseData?.id)}`)
    }
    setLastPathName(null)
  }

  /* istanbul ignore next */
  const CaseNumberCellRenderer = (params) => {
    return (
      <div className="gridCellFlexBox">
        <a
          className="gridLinkclass gridCellDisplayData"
          onClick={() => onCaseNumberClick(params?.data)}
          title={params?.data?.caseNumber}
        >
          {params?.data?.caseNumber || ''}
        </a>
      </div>
    )
  }

  // Fetch saved column preferences from API on mount
  useEffect(() => {
    /* istanbul ignore next */
    const fetchColumnPreferences = async () => {
      try {
        const contractId = selectedContract?.contractId || 0
        const response = await getCoreData(
          APIEndPoints?.UserPreference?.getQuickSearchColumns?.method,
          APIEndPoints?.UserPreference?.getQuickSearchColumns?.url?.replace(
            '__ContractId__',
            contractId
          )
        )
        if (
          response?.status === utils?.apiResponseCode?.successCode &&
          response?.data?.columnOption
        ) {
          const visibleFields = response?.data?.columnOption
            ?.split(',')
            ?.map((f) => fieldNameFromApi[f?.trim()] || f?.trim())
            ?.filter(Boolean)
          if (visibleFields?.length > 0) {
            const savedPrefs = {}
            pickerFields?.forEach((col) => {
              savedPrefs[col?.field] = visibleFields?.includes(col?.field)
            })
            setColumnPreferences(savedPrefs)
          }
        }
      } catch (error) {
        // Fallback to localStorage preferences silently
      }
    }
    fetchColumnPreferences()
  }, [])

  const getColumnVisibility = useCallback(
    (field) => {
      const prefs =
        Object?.keys(columnPreferences)?.length > 0 ? columnPreferences : null
      if (!prefs) return true
      return prefs[field] !== false
    },
    [columnPreferences]
  )

  const buildTextColumnDef = useCallback((field, headerName, hide) => {
    return {
      headerName,
      field,
      filter: 'agTextColumnFilter',
      filterParams: utils?.gridFilterOptions?.commonFilterParams,
      cellRenderer: customTextRenderer,
      hide,
    }
  }, [])

  const buildDateColumnDef = useCallback((field, headerName, hide) => {
    return {
      headerName,
      field,
      filter: 'agDateColumnFilter',
      filterParams: utils?.gridFilterOptions?.dateFilterParams,
      valueGetter: utils?.dateAndTimeConversion,
      comparator: utils?.gridFilterOptions?.dateComparator,
      cellRenderer: /* istanbul ignore next */ (params) =>
        customDateRenderer(params, 'default'),
      hide,
    }
  }, [])

  const datasource = useMemo(() => {
    const columnDefs = []

    pickerFields?.forEach((col) => {
      const hide = !getColumnVisibility(col?.field)
      if (col?.field === 'caseNumber') {
        columnDefs?.push({
          headerName: col?.headerName,
          field: col?.field,
          valueGetter: 'data?.caseNumber',
          filter: 'agTextColumnFilter',
          filterParams: utils?.gridFilterOptions?.commonFilterParams,
          cellRenderer: CaseNumberCellRenderer,
          hide: false,
        })
      } else if (col?.type === 'date') {
        columnDefs?.push(buildDateColumnDef(col?.field, col?.headerName, hide))
      } else {
        columnDefs?.push(buildTextColumnDef(col?.field, col?.headerName, hide))
      }
    })

    return {
      columnDefs,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      rowData: searchData?.dataSource,
      popupParent: popupParentEl || document?.body,
    }
  }, [
    searchData,
    getColumnVisibility,
    buildTextColumnDef,
    buildDateColumnDef,
    popupParentEl,
  ])

  const openColumnFilter = () => {
    const currentState = {}
    pickerFields?.forEach((col) => {
      currentState[col?.field] = getColumnVisibility(col?.field)
    })
    setTempColumnState(currentState)
    setShowColumnFilter(true)
  }

  const cancelColumnFilter = () => {
    setTempColumnState({})
    setShowColumnFilter(false)
  }

  const applyColumnFilter = async () => {
    const newPreferences = { ...tempColumnState, caseNumber: true }
    setColumnPreferences(newPreferences)
    setShowColumnFilter(false)
    /* istanbul ignore next */
    try {
      const contractId = selectedContract?.contractId || 0
      await getCoreData(
        APIEndPoints?.UserPreference?.updateQuickSearchColumns?.method,
        APIEndPoints?.UserPreference?.updateQuickSearchColumns?.url?.replace(
          '__ContractId__',
          contractId
        ),
        {
          columnOption: Object?.keys(newPreferences)
            ?.filter((key) => newPreferences[key])
            ?.map((key) => fieldNameToApi[key] || key)
            ?.join(', '),
        }
      )
    } catch (error) {
      // Preferences are already saved to localStorage as fallback
    }
  }

  const onChangeHandlerFilterCheckbox = (event, field) => {
    setTempColumnState((prev) => ({
      ...prev,
      [field]: event?.target?.checked,
    }))
  }

  const handlePageSizeChange = (event) => {
    const newSize = event?.target?.value
    setPageSize(newSize)
    gridApiRef?.current?.paginationSetPageSize(Number(newSize))
  }

  /* istanbul ignore next */
  const onGridReadyHandler = (params) => {
    gridApiRef.current = params?.api
  }

  const isApplyDisabled = useMemo(() => {
    const currentState = {}
    pickerFields?.forEach((col) => {
      currentState[col?.field] = getColumnVisibility(col?.field)
    })
    return JSON?.stringify(currentState) === JSON?.stringify(tempColumnState)
  }, [tempColumnState, getColumnVisibility])

  return (
    <div className="searchBarResultGridContainer" ref={containerRef}>
      <div className="searchBarResultGridHeader">
        <div className="searchBarPageSizeSection">
          <span className="searchBarPageSizeLabel">Show</span>
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            size="small"
            className="searchBarPageSizeSelect"
            data-testid="searchBarPageSizeSelect"
            MenuProps={{ style: { zIndex: 2010 } }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
          <span className="searchBarPageSizeLabel">entries</span>
        </div>
        <div
          className="searchBarColumnFilterSection"
          onClick={openColumnFilter}
          data-testid="columnOptionsButton"
        >
          <img
            src={ColumnFilterIcon}
            className="searchBarColumnFilterImage"
            alt="Column Options"
          />
          <span className="searchBarColumnFilterLabel">Column Options</span>
        </div>
      </div>
      <div className="searchBarGridWrapper">
        <Grid
          dataSource={datasource}
          data={datasource?.rowData}
          pagination={true}
          paginationPageSize={pageSize}
          onGridReady={onGridReadyHandler}
        />
      </div>

      {showColumnFilter && (
        <Dialog
          id="dialog_SearchBarColumnFilter"
          open={showColumnFilter}
          className="workFlowFilterColumnSection"
          data-testid="columnFilterDialog"
          sx={{ zIndex: 2010 }}
        >
          <DialogTitle>
            <div className="filterColumnHeaderMainSection">
              <div className="filterColumnHeaderLabelSection title">
                Column Filter
              </div>
              <div
                className="filterColumnHeaderCloseSection"
                onClick={cancelColumnFilter}
                data-testid="columnFilterCloseIcon"
              >
                <CloseIcon />
              </div>
            </div>
          </DialogTitle>
          <DialogContent>
            <div className="filterColumnContentMainSection">
              <div className="filterColumnContentSubHeadingSection">
                Filter the columns by checking/unchecking the case fields which
                results in search grid
              </div>
              <div className="filterColumnContentSection">
                {pickerFields?.map((col) => (
                  <div className="filterElementContainer" key={col?.field}>
                    <CheckboxControl
                      field={{
                        id: col?.field,
                        dataTestId: `columnFilter_${col?.field}`,
                        value: col?.alwaysVisible
                          ? true
                          : tempColumnState[col?.field] !== false,
                        fieldLabel: col?.headerName,
                        disabled: col?.alwaysVisible,
                      }}
                      onChangeHandler={(event) =>
                        onChangeHandlerFilterCheckbox(event, col?.field)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <ButtonComponent
              Field={{
                fieldValue: 'Cancel',
                fieldLabel: 'Cancel',
                variant: 'secondary',
              }}
              className="saveButton"
              onClickHandler={cancelColumnFilter}
              dataTestId="columnFilterCancelButton"
            />
            <ButtonComponent
              Field={{
                fieldValue: 'OK',
                fieldLabel: 'OK',
                variant: 'primary',
                disabled: isApplyDisabled,
              }}
              className="saveButton"
              onClickHandler={applyColumnFilter}
              dataTestId="columnFilterOkButton"
            />
          </DialogActions>
        </Dialog>
      )}
    </div>
  )
}

export default SearchBarResultGrid
